import { Button, Typography, Tabs, Tab } from "@kaltura/ds-react-components";
import { ChevronLeft24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import {
    HeaderContainer,
    StyledBackButton,
    CenterSection,
    TitleContainer,
    TabsContainer,
    SaveButtonContainer,
} from "./styledComponents";
import { EditPlaylistTabs } from "../types";
import { TruncatedLine } from "@kaltura/mediaspace-shared-ui";
import { useState } from "react";
import { ConfirmNavigationModal } from "./ConfirmNavigationModal";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface HeaderProps {
    title: string;
    activeTab: EditPlaylistTabs;
    onTabChange: (newValue: EditPlaylistTabs) => void;
    onSave?: () => void;
    processing: boolean;
    showTitle: boolean;
    hasChanges?: boolean;
    playlistId: string;
}

export const EditPlaylistHeader = (props: HeaderProps) => {
    const {
        title,
        activeTab,
        onTabChange,
        onSave,
        processing = false,
        showTitle,
        hasChanges = false,
        playlistId,
    } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleBack = () => {
        // If there are changes, show confirmation modal
        if (hasChanges) {
            setIsModalOpen(true);
        }
        else {
            // No changes, navigate back directly
            window.history.back();
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleConfirmNavigateAway = () => {
        setIsModalOpen(false);
        window.history.back();
    };

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = (actionName: string) => {
        sendButtonAnalytics(
            `Playlist Tab - ${actionName}`,
            ButtonClickAnalyticsType.NAVIGATE,
            playlistId,
            "Playlists",
            "Tabs - Edit Playlist"
        );
    };

    const baseClassName = "kms-ds-edit-playlist-header";

    return (
        <HeaderContainer className={baseClassName}>
            <StyledBackButton variant="borderless" aria-label={translate("Back")} onClick={handleBack}>
                <ChevronLeft24Icon />
            </StyledBackButton>

            <CenterSection>
                {showTitle && (
                    <TitleContainer className={`${baseClassName}-title`}>
                        <TruncatedLine>
                            <Typography variant="body2Highlight" component="h1" noWrap>
                                {title}
                            </Typography>
                        </TruncatedLine>
                    </TitleContainer>
                )}

                <TabsContainer className={`${baseClassName}-tabs`}>
                    <Tabs value={activeTab} onChange={(_, newValue) => onTabChange(newValue)} centered>
                        <Tab
                            value="content"
                            label={translate("Content")}
                            onClick={() => sendAnalytics("Content")}
                            className={`${baseClassName}-tabs-content`}
                        />
                        <Tab
                            value="info"
                            label={translate("Info")}
                            onClick={() => sendAnalytics("Info")}
                            className={`${baseClassName}-tabs-info`}
                        />
                    </Tabs>
                </TabsContainer>

                {/* Empty third column to balance the grid */}
                <div></div>
            </CenterSection>

            <SaveButtonContainer>
                <Button
                    onClick={activeTab === EditPlaylistTabs.INFO ? undefined : onSave}
                    loading={processing}
                    type={activeTab === EditPlaylistTabs.INFO ? "submit" : "button"}
                    form={activeTab === EditPlaylistTabs.INFO ? "playlist-info-form" : undefined}
                    disabled={processing || !hasChanges}
                    className={`${baseClassName}-save-button`}
                >
                    {translate("Save")}
                </Button>
            </SaveButtonContainer>

            <ConfirmNavigationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmNavigateAway}
            />
        </HeaderContainer>
    );
};

export default EditPlaylistHeader;
